import { collection, doc, getDoc, setDoc, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { COLLECTIONS } from '../collections';
import { handleError } from '@/lib/utils/errors';

export async function getDocument<T>(collectionName: string, id: string): Promise<T | null> {
  try {
    const docRef = doc(db, collectionName, id);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? docSnap.data() as T : null;
  } catch (error) {
    throw handleError(error);
  }
}

export async function setDocument<T extends { id?: string }>(
  collectionName: string,
  data: T,
  id?: string,
  options = { merge: false }
): Promise<string> {
  try {
    const docRef = id ? doc(db, collectionName, id) : doc(collection(db, collectionName));
    await setDoc(docRef, data, options);
    return docRef.id;
  } catch (error) {
    throw handleError(error);
  }
}

export async function queryDocuments<T>(
  collectionName: string,
  conditions: {
    field: string;
    operator: '==' | '>' | '<' | '>=' | '<=';
    value: any;
  }[],
  orderByField?: string,
  orderDirection?: 'asc' | 'desc',
  limitCount?: number
): Promise<T[]> {
  try {
    let q = collection(db, collectionName);
    
    // Apply where conditions
    conditions.forEach(({ field, operator, value }) => {
      q = query(q, where(field, operator, value));
    });
    
    // Apply ordering
    if (orderByField) {
      q = query(q, orderBy(orderByField, orderDirection));
    }
    
    // Apply limit
    if (limitCount) {
      q = query(q, limit(limitCount));
    }
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as T[];
  } catch (error) {
    throw handleError(error);
  }
}